import React, { useState } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { facebookApi } from '../services/api';

const PostPage: React.FC = () => {
	usePageTitle('Post');

	const [fuelPrices, setFuelPrices] = useState({
		regular87: '',
		midgrade91: '',
		premium94: '',
		diesel: ''
	});
	const [loading, setLoading] = useState(false);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFuelPrices(prev => ({
			...prev,
			[name]: value
		}));
	};

	const handlePostToFacebook = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoading(true);
		setSuccessMessage(null);
		setErrorMessage(null);

		try {
			const response = await facebookApi.postFuelPrices(fuelPrices);

			if (response.data.success) {
				setSuccessMessage(response.data.message);

				// Clear form after successful post
				setFuelPrices({
					regular87: '',
					midgrade91: '',
					premium94: '',
					diesel: ''
				});
			} else {
				setErrorMessage(response.data.message || 'Failed to post to Facebook');
			}
		} catch (error: any) {
			console.error('Error posting to Facebook:', error);
			const errorMsg = error.response?.data?.message || 'An error occurred while posting to Facebook. Please try again.';
			setErrorMessage(errorMsg);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="p-6">
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900">Post</h1>
				<p className="mt-1 text-sm text-gray-500">
					Post fuel prices and manage your social media posts
				</p>
			</div>

			{/* Post Fuel Price Section */}
			<div className="bg-white rounded-lg shadow overflow-hidden mb-6">
				<div className="px-6 py-4 border-b border-gray-200">
					<h2 className="text-lg font-semibold text-gray-900">Post Fuel Price</h2>
					<p className="mt-1 text-sm text-gray-500">
						Enter current fuel prices to post on Facebook
					</p>
				</div>

				<form onSubmit={handlePostToFacebook}>
					<div className="p-6 space-y-4">
						{successMessage && (
							<div className="rounded-md bg-green-50 p-4">
								<div className="flex">
									<div className="flex-shrink-0">
										<svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
											<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
										</svg>
									</div>
									<div className="ml-3">
										<p className="text-sm font-medium text-green-800">{successMessage}</p>
									</div>
								</div>
							</div>
						)}

						{errorMessage && (
							<div className="rounded-md bg-red-50 p-4">
								<div className="flex">
									<div className="flex-shrink-0">
										<svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
											<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
										</svg>
									</div>
									<div className="ml-3">
										<p className="text-sm font-medium text-red-800">{errorMessage}</p>
									</div>
								</div>
							</div>
						)}

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<label htmlFor="regular87" className="block text-sm font-medium text-gray-700 mb-1">
									Regular (87):
								</label>
								<div className="relative rounded-md shadow-sm">
									<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
										<span className="text-gray-500 sm:text-sm">$</span>
									</div>
									<input
										type="number"
										step="0.01"
										name="regular87"
										id="regular87"
										value={fuelPrices.regular87}
										onChange={handlePriceChange}
										required
										className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
										placeholder="0.00"
									/>
								</div>
							</div>

							<div>
								<label htmlFor="midgrade91" className="block text-sm font-medium text-gray-700 mb-1">
									Midgrade (91):
								</label>
								<div className="relative rounded-md shadow-sm">
									<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
										<span className="text-gray-500 sm:text-sm">$</span>
									</div>
									<input
										type="number"
										step="0.01"
										name="midgrade91"
										id="midgrade91"
										value={fuelPrices.midgrade91}
										onChange={handlePriceChange}
										required
										className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
										placeholder="0.00"
									/>
								</div>
							</div>

							<div>
								<label htmlFor="premium94" className="block text-sm font-medium text-gray-700 mb-1">
									Premium (94):
								</label>
								<div className="relative rounded-md shadow-sm">
									<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
										<span className="text-gray-500 sm:text-sm">$</span>
									</div>
									<input
										type="number"
										step="0.01"
										name="premium94"
										id="premium94"
										value={fuelPrices.premium94}
										onChange={handlePriceChange}
										required
										className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
										placeholder="0.00"
									/>
								</div>
							</div>

							<div>
								<label htmlFor="diesel" className="block text-sm font-medium text-gray-700 mb-1">
									Diesel:
								</label>
								<div className="relative rounded-md shadow-sm">
									<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
										<span className="text-gray-500 sm:text-sm">$</span>
									</div>
									<input
										type="number"
										step="0.01"
										name="diesel"
										id="diesel"
										value={fuelPrices.diesel}
										onChange={handlePriceChange}
										required
										className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
										placeholder="0.00"
									/>
								</div>
							</div>
						</div>
					</div>

					<div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
						<button
							type="submit"
							disabled={loading}
							className={`inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
								} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
						>
							{loading ? (
								<>
									<svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
										<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
										<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
									</svg>
									Posting...
								</>
							) : (
								<>
									<svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
										<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
									</svg>
									Post
								</>
							)}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default PostPage;
